/* eslint-disable jsx-a11y/control-has-associated-label */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBreakpoint } from 'shared/hooks/useBreakpoint';

type ContentSlideType = {
  id: number;
  title: string;
  desktopContentSource: string;
  mobileContentSource: string;
  desktopDescription: string;
  mobileDescription: string;
};

interface AutoSliderProps {
  slideDuration: number;
  slides: ContentSlideType[];
}
export const AutoSlider: FC<AutoSliderProps> = ({ slideDuration, slides }) => {
  const [currentSlideId, setCurrentSlideId] = useState(slides[0]?.id || 0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useBreakpoint('lg');

  const findSlideIndexById = useCallback(
    (id: number) => slides.findIndex((slide) => slide.id === id),
    [slides],
  );

  const startInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentSlideId((prevId) => {
        const currentIndex = findSlideIndexById(prevId);
        const nextIndex = (currentIndex + 1) % slides.length;
        return slides[nextIndex].id;
      });
    }, slideDuration);
  }, [slideDuration, slides]);

  const goToSlide = useCallback(
    (id: number) => {
      setCurrentSlideId(id);
      startInterval();
    },
    [startInterval],
  );

  const currentIndex = useMemo(() => findSlideIndexById(currentSlideId), [currentSlideId]);

  useEffect(() => {
    startInterval();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startInterval]);

  return (
    <div className="relative -mx-4 flex h-full w-auto flex-col items-center justify-end gap-4 bg-transparent text-white lg:-mx-12">
      <div className="relative h-full w-full overflow-hidden">
        <div
          className="flex w-full transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {slides.map((slide) => (
            <div
              key={slide.id}
              className="flex w-full flex-shrink-0 flex-col items-center justify-end"
            >
              <h2 className="pb-14 text-2xl font-extrabold text-lightBlue lg:pt-[7px] lg:text-3xl lg:font-semibold">
                {slide.title}
              </h2>
              <div className="h-full lg:w-auto lg:max-w-110.5">
                <object
                  type="image/svg+xml"
                  data={isMobile ? slide.mobileContentSource : slide.desktopContentSource}
                  className="h-full w-full"
                  aria-label="Illustration"
                />
              </div>
              <p className="whitespace-pre-line pt-15 text-center text-sm font-thin text-gray-300 lg:pt-20">
                {isMobile ? slide.mobileDescription : slide.desktopDescription}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="pb-10.5 flex gap-1.5 lg:pb-30">
        {slides.map((slide) => (
          <button
            key={slide.id}
            onClick={() => goToSlide(slide.id)}
            className={`rounded-full transition-all duration-300 ${
              slide.id === currentSlideId ? 'h-1.5 w-6 bg-white' : 'h-1.5 w-1.5 bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};
